import SlabRevealAction, { useSlabReveal } from './SlabRevealAction';
import { slabImageUrl } from './imageUrls.mjs';
import { useCallback, useDeferredValue, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, useDragControls, useReducedMotion } from 'framer-motion';
import {
  IconAdjustmentsHorizontal, IconArrowsExchange, IconCheck, IconChevronLeft, IconChevronRight, IconCopy, IconCube, IconExternalLink,
  IconBell, IconDots, IconEye, IconEyeOff, IconLayoutGrid, IconMapPin, IconPencil, IconRefresh, IconRotate, IconSearch, IconSelector, IconX,
} from '@tabler/icons-react';
import '@fontsource-variable/archivo/wdth.css';
import Showcase3D, { SlabScan } from './Showcase3D';
import FxNote from '../inventory/FxNote';
import {
  EMPTY_FILTERS, SORTS, activeFilterCount, count, facetsFor, filterByInventoryScope, isHiddenByInventoryFilters, money, normaliseSlabs, selectSlabs, shortDate,
  referenceSale, signedMoney, sourceLabel, totals, toNumber, withPriceReference,
  hasValue, minimumValue, saleMoney, slabMoney,
} from './slabModel.mjs';
import './SlabViewer.css';
import SlabPriceCheck from './SlabPriceCheck';
import SlabPurchaseCost from './SlabPurchaseCost';
import SlabCategoryEdit from './SlabCategoryEdit';
import SlabFilters, { FILTER_TITLES, selectedFilterValues } from './SlabFilters';
import TradeBuilder from './TradeBuilder';
import SaleLinkForm from './SaleLinkForm';
import SlabShopifyForm from './SlabShopifyForm';
import PickBuilder from './PickBuilder';
import { useWallWindow } from './useWallWindow';
import { readBrowseSession, saveBrowseSession } from './browseSession.mjs';
import { INTENDED_USES } from '../inventory/inventoryModel.mjs';

const listedMoney = (value) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value);

function readPreference(key, fallback, allowed) {
  try {
    const value = window.localStorage.getItem(key);
    return value && (!allowed || allowed.includes(value)) ? value : fallback;
  } catch {
    return fallback;
  }
}

function writePreference(key, value) {
  try { window.localStorage.setItem(key, value); } catch { /* private mode */ }
}

function useWideLayout() {
  const query = '(min-width: 960px)';
  const [wide, setWide] = useState(() => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false));
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const list = window.matchMedia(query);
    const update = () => setWide(list.matches);
    update();
    list.addEventListener?.('change', update);
    return () => list.removeEventListener?.('change', update);
  }, []);
  return wide;
}

function GradeMark({ slab, size = 'regular' }) {
  if (!slab.grade) return null;
  return (
    <span className={`sv-grade sv-grade-${size}`} role="img" aria-label={`${slab.grader} ${slab.grade}${slab.gradeLabel ? ` ${slab.gradeLabel}` : ''}`}>
      <span>{slab.grader}</span>
      <strong>{slab.grade}</strong>
    </span>
  );
}

function Fact({ label, compactLabel, value, note, tone }) {
  return (
    <div className={`sv-fact${tone ? ` sv-tone-${tone}` : ''}`}>
      <span className={compactLabel ? 'sv-desktop-only' : undefined}>{label}</span>
      {compactLabel && <span className="sv-mobile-only">{compactLabel}</span>}
      <strong>{value}</strong>
      {note ? <em>{note}</em> : null}
    </div>
  );
}

function StorageLocation({ slab, compact=false }) {
  const location=slab.storageLocation;
  return <div className={`sv-storage-location${compact?' is-compact':''}`} aria-label="Saved storage location">
    <IconMapPin size={compact?13:16} stroke={1.7} aria-hidden="true"/>
    <span><strong>{compact?location.compact:location.label}</strong>{!compact&&location.detail&&<small>{location.detail}</small>}</span>
  </div>;
}

function slabFacts(slab) {
  const facts = [];
  for (const grader of ['ACE', 'PSA']) {
    const sale = referenceSale(slab, grader);
    facts.push({ key: `sale-${grader}`, label: `${grader} ${slab.grade} last sold`, value: saleMoney(sale), note: sale ? `${shortDate(sale.date)}${sale.status==='range'?' · same-day sales':''}` : 'No cached sale' });
  }
  if(slab.shopifyListing)facts.push({key:'shopify-listing',label:'Shopify listed',value:listedMoney(slab.shopifyListing.priceGbp),note:'Asking price'});
  if (slab.pop?.atGrade != null) facts.push({ key: 'pop', label: 'Pop at this grade', value: count(slab.pop.atGrade) });
  if (slab.pop?.higher != null) facts.push({ key: 'higher', label: 'Graded higher', value: count(slab.pop.higher) });
  if (slab.pop?.total != null) facts.push({ key: 'total', label: `Total ${slab.grader || ''} graded`.replace('  ', ' '), value: count(slab.pop.total) });
  if (slab.profit != null) facts.push({ key: 'profit', label: 'Profit', value: signedMoney(slab.profit), note: `Paid ${money(slab.cost)}`, tone: slab.profit >= 0 ? 'up' : 'down' });
  else if (slab.cost != null) facts.push({ key: 'cost', label: 'Paid', value: money(slab.cost) });
  return facts;
}

function SlabInfo({ slab, flipped, onFlip, onOpen, onCategory, onSaleLink, onShopify, categoryEditor, revealControl }) {
  const facts = slabFacts(slab);
  const subtitle = [slab.set, slab.number ? `#${slab.number}` : ''].filter(Boolean).join(' ');
  return (
    <section className="sv-info" aria-label="Selected slab" aria-live="polite">
      <button type="button" className="sv-lot" onClick={onOpen} aria-label={`${slab.name}${slab.gradeKey ? `, ${slab.gradeKey}` : ''}, ${slabMoney(slab)}. Open details.`}>
        <GradeMark slab={slab} />
        <span className="sv-lot-title">
          <strong>{slab.name}</strong>
          <span>
            {subtitle ? <span>{subtitle}</span> : null}
            {slab.language ? <em>{slab.language}</em> : null}
          </span>
        </span>
        <span className={`sv-lot-value${slab.value==null&&hasValue(slab)?' is-range':''}`}>
          <strong>{slabMoney(slab)}</strong>
          <span>{hasValue(slab) ? slab.valueLabel : 'No cached sale'}</span>
        </span>
      </button>
      <div className="sv-location-row">
        <div className="sv-desktop-only"><StorageLocation slab={slab}/></div>
        <div className="sv-mobile-only"><StorageLocation slab={slab} compact/></div>
        {onShopify&&<button type="button" className="sv-shopify-action" onClick={onShopify}>Shopify</button>}
        <button type="button" className="sv-mobile-details sv-mobile-only" onClick={onOpen}>Details<IconChevronRight size={15} aria-hidden="true"/></button>
      </div>
      {categoryEditor || <div className="sv-facts">
        {facts.map((fact) => <Fact key={fact.key} label={fact.label} compactLabel={fact.key.startsWith('sale-') ? fact.label.replace(' last sold', '') : null} value={fact.value} note={fact.note} tone={fact.tone} />)}
        <Fact label="Cert" value={slab.cert ? `#${slab.cert}` : '—'} />
      </div>}
      {!categoryEditor && <div className="sv-info-actions">
        <button type="button" className="sv-pill" onClick={onFlip} aria-pressed={flipped}>
          <IconRotate size={16} stroke={1.7} />
          {flipped ? 'Show front' : 'Show back'}
        </button>
        <button type="button" className="sv-pill" onClick={onOpen}>All details</button>
      </div>}
      {!categoryEditor && <SlabRevealAction control={revealControl} />}
      {!categoryEditor&&onSaleLink&&<button type="button" className="sv-pill sv-desktop-only" onClick={onSaleLink}>Add sale link</button>}
      {!categoryEditor&&onCategory&&<button type="button" className="sv-pill sv-category-action sv-desktop-only" onClick={onCategory} aria-label={`Change category: ${slab.category}`}>Category · {slab.category} ⌄</button>}
    </section>
  );
}

function Scrubber({ index, total, onChange }) {
  const progress = total > 1 ? (index / (total - 1)) * 100 : 0;
  return (
    <div className="sv-scrubber">
      <button type="button" className="sv-round" onClick={() => onChange(index - 1)} disabled={index <= 0} aria-label="Previous slab">
        <IconChevronLeft size={20} stroke={1.7} />
      </button>
      <input
        type="range"
        min={0}
        max={Math.max(0, total - 1)}
        step={1}
        value={index}
        onChange={(event) => onChange(Number(event.target.value))}
        aria-label="Jump through slabs"
        aria-valuetext={`Slab ${index + 1} of ${total}`}
        style={{ '--sv-progress': `${progress}%` }}
      />
      <button type="button" className="sv-round" onClick={() => onChange(index + 1)} disabled={index >= total - 1} aria-label="Next slab">
        <IconChevronRight size={20} stroke={1.7} />
      </button>
      <span className="sv-position"><strong>{count(index + 1)}</strong> of {count(total)}</span>
    </div>
  );
}

function SlabWall({ slabs, focusIndex, onPick, onPosition }) {
  const { wall, grid, range, columns, heights, onScroll } = useWallWindow(slabs, focusIndex, onPosition);
  return (
    <div className="sv-wall" ref={wall} onScroll={onScroll}>
      <ul className="sv-wall-grid" ref={grid} style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gridTemplateRows: heights.map(height => `${height}px`).join(' ') }}>
        {slabs.slice(range.start, range.end).map((slab, offset) => {
          const position = range.start + offset;
          return <li key={slab.key} data-wall-row={Math.floor(position / columns)} aria-posinset={position + 1} aria-setsize={slabs.length} style={{ gridRow: Math.floor(position / columns) + 1, gridColumn: position % columns + 1 }}>
            <button type="button" className={`sv-tile${position === focusIndex ? ' is-focus' : ''}`} onClick={() => onPick(position)}>
              <div className="sv-tile-scan">
                <SlabScan src={slabImageUrl(slab, 'front', 320)} side="front" slab={slab} eager />
              </div>
              <span className="sv-tile-name">{slab.name}</span>
              <span className="sv-tile-meta">
                <em>{slab.gradeKey || slab.set}</em>
              </span>
              <StorageLocation slab={slab} compact/>
              {['ACE', 'PSA'].map(grader => (
                <span className="sv-tile-meta" key={grader}>
                  <em>{grader}</em><strong>{saleMoney(referenceSale(slab, grader))}</strong>
                </span>
              ))}
              {slab.shopifyListing&&<span className="sv-tile-meta"><em>Shopify listed</em><strong>{listedMoney(slab.shopifyListing.priceGbp)}</strong></span>}
            </button>
          </li>;
        })}
      </ul>
    </div>
  );
}

function Sheet({ label, title, onClose, onBack, wide, footer, className = '', children }) {
  const controls = useDragControls();
  const closeButton = useRef(null);
  useEffect(() => { closeButton.current?.focus({ preventScroll: true }); }, []);
  const motionProps = wide
    ? { initial: { x: '100%' }, animate: { x: 0 }, exit: { x: '100%' } }
    : {
      initial: { y: '100%' }, animate: { y: 0 }, exit: { y: '100%' },
      drag: 'y', dragControls: controls, dragListener: false, dragConstraints: { top: 0, bottom: 0 }, dragElastic: { top: 0, bottom: 0.7 },
      onDragEnd: (event, info) => { if (info.offset.y > 110 || info.velocity.y > 650) onClose(); },
    };
  return (
    <motion.div className="sv-layer" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.18 }}>
      <div className="sv-scrim" onClick={onClose} aria-hidden="true" />
      <motion.section
        className={`sv-sheet${wide ? ' is-side' : ''}${className ? ` ${className}` : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        transition={{ type: 'spring', stiffness: 360, damping: 36 }}
        {...motionProps}
      >
        <header className="sv-sheet-head" onPointerDown={(event) => { if (!wide && !event.target.closest('button')) controls.start(event); }}>
          {!wide ? <div className="sv-handle" aria-hidden="true" /> : null}
          {onBack && <button type="button" className="sv-round sv-sheet-back" onClick={onBack} aria-label="Back to all filters"><IconChevronLeft size={20} stroke={1.7} /></button>}
          <div className="sv-sheet-title">{title}</div>
          <button type="button" ref={closeButton} className="sv-round" onClick={onClose} aria-label="Close"><IconX size={18} stroke={1.7} /></button>
        </header>
        <div className="sv-sheet-body">{children}</div>
        {footer ? <footer className="sv-sheet-foot">{footer}</footer> : null}
      </motion.section>
    </motion.div>
  );
}

function DetailRows({ rows }) {
  const visible = rows.filter((row) => row && row.value !== '' && row.value != null);
  if (!visible.length) return null;
  return (
    <dl className="sv-rows">
      {visible.map((row) => (
        <div key={row.label}>
          <dt>{row.label}</dt>
          <dd className={row.tone ? `sv-tone-${row.tone}` : undefined}>{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function SlabDetails({ slab,fx }) {
  const [copied, setCopied] = useState(false);
  async function copyCert() {
    try { await navigator.clipboard.writeText(slab.cert); setCopied(true); window.setTimeout(() => setCopied(false), 1600); } catch { /* clipboard blocked */ }
  }
  return (
    <div className="sv-details">
      <div className="sv-details-scans">
        {['front', 'back'].map((side) => (
          <figure key={side}>
            <div className="sv-details-scan"><SlabScan src={slab.images[side]} side={side} slab={slab} eager /></div>
            <figcaption>
              {side === 'front' ? 'Front' : 'Back'}
              {/^https?:/.test(slab.images[side] || '') ? <a href={slab.images[side]} target="_blank" rel="noreferrer" aria-label={`Open full-size ${side} scan`}><IconExternalLink size={14} stroke={1.7} /></a> : null}
            </figcaption>
          </figure>
        ))}
      </div>
      <div className="sv-details-value">
        <strong>{slabMoney(slab)}</strong>
        <span>{hasValue(slab) ? `${slab.valueLabel}${slab.valueAsOf ? `, ${shortDate(slab.valueAsOf)}` : ''}` : 'No cached sale'}</span>
      </div>
      {slab.sales.length ? (
        <section className="sv-details-group">
          <h3>Recent sales</h3>
          <DetailRows rows={slab.sales.map((sale) => ({ label: `${sale.grader}${sale.grade ? ` ${sale.grade}` : ''}${sale.sameGrader ? '' : ' (other grader)'}`, value: `${saleMoney(sale)}${sale.date ? `, ${shortDate(sale.date)}` : ''}${sale.status==='range'?' · same-day sales':''}` }))} />
        </section>
      ) : null}
      {slab.shopifyListing&&<section className="sv-details-group"><h3>Shopify listing</h3><DetailRows rows={[{label:'Listed price',value:`${listedMoney(slab.shopifyListing.priceGbp)} · asking price`},{label:'Price checked',value:shortDate(slab.shopifyListing.checkedAt)}]}/>{slab.shopifyListing.url&&<p><a href={slab.shopifyListing.url} target="_blank" rel="noreferrer">View Shopify listing ↗</a></p>}</section>}
      {[slab.aceReference,slab.psaReference].filter(Boolean).some(s=>s.url)&&<section className="sv-details-group"><h3>Sale sources</h3>{[slab.aceReference,slab.psaReference].filter(s=>s?.url).map(s=><p key={s.grader}><a href={s.url} target="_blank" rel="noreferrer">{s.grader} {s.grade} · {s.verification==='owner_confirmed'?'Owner confirmed':'Provider reported'} ↗</a></p>)}</section>}
      <section className="sv-details-group"><h3>Currency conversion</h3><FxNote fx={fx}/></section>
      {slab.pop ? (
        <section className="sv-details-group">
          <h3>Population</h3>
          <DetailRows rows={[
            { label: 'At this grade', value: slab.pop.atGrade != null ? count(slab.pop.atGrade) : '' },
            { label: 'Graded higher', value: slab.pop.higher != null ? count(slab.pop.higher) : '' },
            { label: `Total ${slab.grader} graded`, value: slab.pop.total != null ? count(slab.pop.total) : '' },
          ]} />
        </section>
      ) : null}
      <section className="sv-details-group">
        <h3>Slab</h3>
        <dl className="sv-rows">
          <div>
            <dt>Cert</dt>
            <dd>{slab.cert ? <button type="button" className="sv-copy" onClick={copyCert}>#{slab.cert}{copied ? <IconCheck size={14} stroke={2} /> : <IconCopy size={14} stroke={1.7} />}<span className="sv-sr">{copied ? 'Copied' : 'Copy cert number'}</span></button> : 'Not recorded'}</dd>
          </div>
        </dl>
        <DetailRows rows={[
          { label: 'Grade', value: [slab.gradeKey, slab.gradeLabel].filter(Boolean).join(' ') },
          { label: 'Guard', value: slab.hasGuard===true?'On':slab.hasGuard===false?'None':'Not recorded' },
          { label: 'Set', value: slab.set },
          { label: 'Number', value: slab.number ? `#${slab.number}` : '' },
          { label: 'Year', value: slab.year || '' },
          { label: 'Language', value: slab.language },
          { label: 'Rarity', value: slab.rarity || 'Not recorded' },
          { label: 'Variant / finish', value: slab.variant },
          { label: 'Edition', value: slab.edition || 'Not recorded' },
          { label: 'Additional information', value: slab.additionalInformation },
          { label: 'Collection', value: slab.category },
          { label: 'Held in', value: sourceLabel(slab.source) },
        ]} />
      </section>
      <section className="sv-details-group"><h3>Storage location</h3><StorageLocation slab={slab}/>{slab.physicalIssue&&<p>Physical problem: {slab.physicalIssue.kind==='damaged'?'Damage hold':'Missing slab'}. Ownership and value are retained.</p>}<p><a className="sv-pill" href={`/slab-inventory/picks?report=${encodeURIComponent(slab.cert)}`}>{slab.physicalIssue?'Review physical problem':'Report a missing or damaged slab'}</a></p></section>
      {slab.ownSubmission&&<section className="sv-details-group"><h3>Your ACE submission</h3><DetailRows rows={[{label:'Submission',value:slab.ownSubmission.number},{label:'Name',value:slab.ownSubmission.name}]}/></section>}
      {slab.cost != null || slab.supplier || slab.location || slab.notes || slab.tags.length || slab.intakeGroup ? (
        <section className="sv-details-group">
          <h3>Purchase</h3>
          <DetailRows rows={[
            { label: 'Paid', value: slab.cost != null ? money(slab.cost) : '' },
            { label: 'Profit', value: slab.profit != null ? signedMoney(slab.profit) : '', tone: slab.profit == null ? '' : slab.profit >= 0 ? 'up' : 'down' },
            { label: 'Supplier', value: slab.supplier },
            { label: 'Purchase location', value: slab.location },
            { label: 'Notes', value: slab.notes },
            { label: 'Labels', value: slab.tags.join(', ') },
            { label: 'Purchase group', value: slab.intakeGroup?.label },
            { label: 'Bundle total', value: slab.intakeGroup?.kind==='bundle'?money(slab.intakeGroup.totalGbp):'' },
          ]} />
        </section>
      ) : null}
    </div>
  );
}

function StageMessage({ title, body, actions }) {
  return (
    <div className="sv-message">
      <div className="sv-message-slab" aria-hidden="true" />
      <h2>{title}</h2>
      {body ? <p>{body}</p> : null}
      {actions ? <div className="sv-message-actions">{actions}</div> : null}
    </div>
  );
}

export default function SlabViewer({ records, fx, status = 'ready', error = '', notices = [], onRetry, allowPhoneTilt = true, onSyncSubmissions, submissionSync, onChangeCategory, onSaleLink, onPriceCheck, onPriceRead, tradeDraftKey, browseStateKey, shopify, pickBuilder, reveal, whatnot, purchaseCosts }) {
  const reduceMotion = useReducedMotion();
  const wide = useWideLayout();
  const canonical = useMemo(() => filterByInventoryScope(normaliseSlabs(records)), [records]);
  const [resume] = useState(() => { try { return browseStateKey ? readBrowseSession(window.sessionStorage, browseStateKey) : null; } catch { return null; } });
  const [query, setQuery] = useState(resume?.query || '');
  const deferredQuery = useDeferredValue(query);
  const [filters, setFilters] = useState(resume?.filters || EMPTY_FILTERS);
  const all = useMemo(() => withPriceReference(canonical, filters.priceBasis), [canonical, filters.priceBasis]);
  const [sort, setSort] = useState(() => readPreference('sv-sort', 'value-desc', SORTS.map((option) => option.id)));
  const [view, setView] = useState(() => readPreference('sv-view', 'showcase', ['showcase', 'wall']));
  const [totalHidden, setTotalHidden] = useState(() => readPreference('sv-total-hidden', 'false', ['true', 'false']) === 'true');
  const [editMode, setEditMode] = useState(false);
  const [editFeedback, setEditFeedback] = useState(null);
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [sheet, setSheet] = useState(null);
  const [filterPicker, setFilterPicker] = useState(null);
  const [saleTarget,setSaleTarget]=useState(null);
  const [shopifyTarget,setShopifyTarget]=useState(null);
  const [categoryTarget,setCategoryTarget]=useState(null),[categoryBusy,setCategoryBusy]=useState(false),[categoryError,setCategoryError]=useState('');
  const [retainedSlab, setRetainedSlab] = useState(null);
  const categoryLock=useRef(false);
  const searchInput = useRef(null);
  const updatesTrigger = useRef(null);
  const focusKey = useRef(resume?.selectedKey || null);

  const filteredResults = useMemo(() => selectSlabs(all, { query: deferredQuery, filters, sort }), [all, deferredQuery, filters, sort]);
  const retained = retainedSlab && retainedSlab.query === query && retainedSlab.filters === filters && retainedSlab.sort === sort
    ? all.find(slab => slab.key === retainedSlab.key) : null;
  const keptOutsideFilter = !!retained && !filteredResults.some(slab => slab.key === retained.key);
  const results = useMemo(() => {
    if (!keptOutsideFilter) return filteredResults;
    const next = [...filteredResults];
    next.splice(Math.min(retainedSlab.index, next.length), 0, retained);
    return next;
  }, [filteredResults, keptOutsideFilter, retained, retainedSlab]);
  const scoped = useMemo(() => filterByInventoryScope(all), [all]);
  const facets = useMemo(() => facetsFor(scoped), [scoped]);
  const summary = useMemo(() => totals(filteredResults), [filteredResults]);
  const filterCount = activeFilterCount(filters);
  const current = results[index] || null;
  const revealControl = useSlabReveal(current, reveal?.request);

  // Keep the slab you were looking at when the results change, if it is still in them.
  const [shownResults, setShownResults] = useState(results);
  if (shownResults !== results) {
    setShownResults(results);
    const kept = focusKey.current ? results.findIndex((slab) => slab.key === focusKey.current) : -1;
    setIndex(kept >= 0 ? kept : Math.max(0,Math.min(index,results.length-1)));
  }
  useEffect(() => { if (current) focusKey.current = current.key; }, [current]);
  useEffect(() => { setFlipped(false); }, [current?.key]);
  useEffect(() => { if (!categoryLock.current) setEditFeedback(null); }, [current?.key]);
  useEffect(() => { writePreference('sv-sort', sort); }, [sort]);
  useEffect(() => { writePreference('sv-view', view); }, [view]);
  useEffect(() => { setRetainedSlab(null); }, [query, filters, sort]);

  function toggleTotal() {
    const hidden = !totalHidden;
    writePreference('sv-total-hidden', String(hidden));
    setTotalHidden(hidden);
  }

  const browseSnapshot = useRef(null);
  browseSnapshot.current = { query, filters, selectedKey: current?.key || focusKey.current };
  const persistBrowse = useCallback(() => { try { saveBrowseSession(window.sessionStorage, browseStateKey, browseSnapshot.current); } catch { /* private mode */ } }, [browseStateKey]);
  useEffect(() => {
    if (status !== 'ready') return undefined;
    const timer = window.setTimeout(persistBrowse, 150);
    return () => window.clearTimeout(timer);
  }, [query, filters, current?.key, status, persistBrowse]);
  useEffect(() => {
    window.addEventListener('pagehide', persistBrowse);
    document.addEventListener('visibilitychange', persistBrowse);
    return () => { persistBrowse(); window.removeEventListener('pagehide', persistBrowse); document.removeEventListener('visibilitychange', persistBrowse); };
  }, [persistBrowse]);

  const [jump, setJump] = useState({ to: 0, seq: 0 });
  const changeIndex = useCallback((next, command) => {
    let target = Math.max(0, Math.min(results.length - 1, next));
    const slab = results[target];
    focusKey.current = slab?.key || null;
    if (retainedSlab && slab?.key !== retainedSlab.key) {
      setRetainedSlab(null);
      target = Math.max(0, filteredResults.findIndex(item => item.key === slab?.key));
    }
    setIndex(target);
    if (command) setJump((previous) => ({ to: target, seq: previous.seq + 1 }));
  }, [results, filteredResults, retainedSlab]);
  const goToIndex = useCallback(next => changeIndex(next, true), [changeIndex]);
  const reportIndex = useCallback(next => changeIndex(next, false), [changeIndex]);
  const releaseRetainedSlab = useCallback(() => {
    if (!retainedSlab) return;
    const target = Math.max(0, Math.min(index, filteredResults.length - 1));
    focusKey.current = filteredResults[target]?.key || null;
    setRetainedSlab(null); setIndex(target);
    setJump(previous => ({ to: target, seq: previous.seq + 1 }));
  }, [retainedSlab, filteredResults, index]);
  const closeSheet = useCallback(() => {
    if (sheet === 'shopify') releaseRetainedSlab();
    setSheet(null);
    if (sheet === 'updates') updatesTrigger.current?.focus({ preventScroll: true });
  }, [sheet, releaseRetainedSlab]);
  function retainCurrentSlab() {
    if (current) setRetainedSlab({ key: current.key, index, query, filters, sort });
  }
  const flip = useCallback(() => setFlipped((value) => !value), []);
  const openDetails = useCallback(() => setSheet('details'), []);
  function openSaleLink(){focusKey.current=current?.key;goToIndex(index);setSaleTarget(current);setSheet('sale-link');}
  function openShopify(){if(!current)return;retainCurrentSlab();focusKey.current=current.key;goToIndex(index);setShopifyTarget(current);setSheet('shopify');}
  function openCategory(){retainCurrentSlab();focusKey.current=current?.key;goToIndex(index);setCategoryTarget(current);setCategoryError('');setSheet('category');}
  async function saveCategory(intendedUse){
    if(categoryLock.current||!categoryTarget)return;
    categoryLock.current=true;setCategoryBusy(true);setCategoryError('');
    try{await onChangeCategory(categoryTarget,intendedUse);setSheet(null);}
    catch(error){setCategoryError(error.message||'The category could not be saved.');}
    finally{categoryLock.current=false;setCategoryBusy(false);}
  }
  async function quickSaveCategory(intendedUse) {
    const target = current;
    if (categoryLock.current || !onChangeCategory || target?.source !== 'business' || !target.id || !target.updatedAt || target.intendedUse === intendedUse) return;
    retainCurrentSlab();
    categoryLock.current = true; setCategoryBusy(true);
    setEditFeedback({ status: 'saving', message: `Saving ${target.name} · #${target.cert}…` });
    try {
      await onChangeCategory(target, intendedUse, { quiet: true });
      setEditFeedback({ status: 'saved', message: `Saved #${target.cert} → ${INTENDED_USES[intendedUse]}` });
    } catch (error) {
      setEditFeedback({ status: 'error', message: `Could not change #${target.cert}: ${error.message || 'Please try again.'}` });
    } finally { categoryLock.current = false; setCategoryBusy(false); }
  }

  useEffect(() => {
    function onKey(event) {
      const target = event.target;
      const typing = target.closest?.('input, textarea, select, [contenteditable="true"]');
      if (event.key === 'Escape') {
        if(categoryBusy)return;
        if (sheet === 'filters' && filterPicker) setFilterPicker(null);
        else if (sheet) closeSheet();
        else if (typing === searchInput.current) searchInput.current.blur();
        return;
      }
      if (typing || sheet || event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key === '/') { event.preventDefault(); searchInput.current?.focus(); return; }
      if (view !== 'showcase' || !results.length) return;
      if (event.key === 'ArrowRight') { event.preventDefault(); goToIndex(index + 1); }
      else if (event.key === 'ArrowLeft') { event.preventDefault(); goToIndex(index - 1); }
      else if (target.closest?.('button, a')) return;
      else if (event.key === 'f' || event.key === ' ') { event.preventDefault(); flip(); }
      else if (event.key === 'Enter') { event.preventDefault(); openDetails(); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [flip, goToIndex, index, openDetails, results.length, sheet, filterPicker, view, categoryBusy, closeSheet]);

  const quickFilters = useMemo(() => {
    const chips = [];
    for (const [kind,title] of [['hiddenCategories','Hidden category'],['hiddenLabels','Hidden label']]) {
      for (const key of filters[kind]||[]) chips.push({id:`${kind}:${key}`,label:`${title}: ${key}`,active:()=>true,toggle:f=>({...f,[kind]:(f[kind]||[]).filter(value=>value!==key)})});
    }
    if (filters.hiddenNoteQuery?.trim()) chips.push({id:'hiddenNoteQuery',label:`Hidden notes: ${filters.hiddenNoteQuery.trim()}`,active:()=>true,toggle:f=>({...f,hiddenNoteQuery:''})});
    for (const [kind,title] of [['sets','Set'],['rarities','Rarity'],['editions','Edition'],['variants','Variant'],['years','Year']]) {
      for (const key of filters[kind]||[]) chips.push({id:`${kind}:${key}`,label:`${title}: ${key}`,count:facets[kind].find(option=>option.key===key)?.count,active:()=>true,toggle:f=>({...f,[kind]:(f[kind]||[]).filter(value=>value!==key)})});
    }
    if (facets.sources.length > 1) {
      for (const source of facets.sources) chips.push({ id: `source:${source.key}`, label: sourceLabel(source.key), count: source.count, active: (f) => f.source === source.key, toggle: (f) => ({ ...f, source: f.source === source.key ? 'all' : source.key }) });
    }
    for (const grade of [...facets.grades].sort((a, b) => b.count - a.count).slice(0, 4)) {
      chips.push({ id: `grade:${grade.key}`, label: grade.key, count: grade.count, active: (f) => f.grades.includes(grade.key), toggle: (f) => ({ ...f, grades: f.grades.includes(grade.key) ? f.grades.filter((item) => item !== grade.key) : [...f.grades, grade.key] }) });
    }
    for (const language of facets.languages.filter((item) => !['English', 'Unknown'].includes(item.key)).slice(0, 2)) {
      chips.push({ id: `language:${language.key}`, label: language.key, count: language.count, active: (f) => f.languages.includes(language.key), toggle: (f) => ({ ...f, languages: f.languages.includes(language.key) ? f.languages.filter((item) => item !== language.key) : [...f.languages, language.key] }) });
    }
    const thousandPlus = scoped.filter((slab) => minimumValue(slab) != null && minimumValue(slab) >= 1000).length;
    if (thousandPlus && thousandPlus < scoped.length) {
      chips.push({ id: 'value:1000', label: '£1,000 and up', count: thousandPlus, active: (f) => toNumber(f.min) === 1000 && !f.max, toggle: (f) => (toNumber(f.min) === 1000 && !f.max ? { ...f, min: '' } : { ...f, min: '1000', max: '' }) });
    }
    return chips;
  }, [scoped, facets, filters]);

  const loading = status === 'loading' && !all.length;
  const failed = status === 'error' && !all.length;
  const resetEverything = () => { setQuery(''); setFilters(EMPTY_FILTERS); };
  const categoryEditor = editMode && onChangeCategory && current ? <SlabCategoryEdit slab={current} busy={categoryBusy} feedback={editFeedback} onChange={quickSaveCategory} onRefresh={onRetry} /> : null;

  let body;
  if (loading) {
    body = <StageMessage title="Bringing your slabs out" body="Loading inventory and vault." />;
  } else if (failed) {
    body = <StageMessage title="Slabs didn't load" body={error || 'The inventory service did not respond.'} actions={onRetry ? <button type="button" className="sv-primary" onClick={onRetry}><IconRefresh size={16} stroke={1.8} />Try again</button> : null} />;
  } else if (!all.length) {
    body = <StageMessage title="No slabs yet" body="Slabs added to inventory or your vault appear here." />;
  } else if (!results.length) {
    body = (
      <StageMessage
        title="Nothing matches"
        body={query ? `No slab matches “${query.trim()}”${filterCount ? ' with these filters' : ''}.` : 'No slab matches these filters.'}
        actions={(
          <>
            {query ? <button type="button" className="sv-secondary" onClick={() => setQuery('')}>Clear search</button> : null}
            {filterCount ? <button type="button" className="sv-secondary" onClick={() => setFilters(EMPTY_FILTERS)}>Reset filters</button> : null}
          </>
        )}
      />
    );
  } else if (view === 'wall') {
    body = <SlabWall slabs={results} focusIndex={index} onPosition={reportIndex} onPick={(position) => { goToIndex(position); setView('showcase'); }} />;
  } else {
    body = (
      <div className="sv-showcase">
        <Showcase3D slabs={results} index={index} jump={jump} onIndexChange={reportIndex} flipped={flipped} onFlip={flip} resetKey={results} reduceMotion={reduceMotion} allowPhoneTilt={allowPhoneTilt} />
        {current ? <SlabInfo slab={current} flipped={flipped} onFlip={flip} onOpen={openDetails} onSaleLink={onSaleLink?openSaleLink:null} onShopify={shopify?openShopify:null} onCategory={onChangeCategory&&current.source==='business'&&current.updatedAt?openCategory:null} categoryEditor={categoryEditor} revealControl={revealControl} /> : null}
        <Scrubber index={index} total={results.length} onChange={goToIndex} />
      </div>
    );
  }

  return (
    <div className="sv-root" data-view={view} data-editing={editMode || undefined}>
      <header className="sv-top">
        <div className="sv-bar">
          <label className="sv-search">
            <IconSearch size={18} stroke={1.7} aria-hidden="true" />
            <input
              ref={searchInput}
              type="search"
              enterKeyHint="search"
              autoComplete="off"
              autoCorrect="off"
              spellCheck="false"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search card, set, rarity…"
              aria-label="Search by card, set, rarity, edition, number, cert or grade"
            />
            {query ? <button type="button" className="sv-clear" onClick={() => { setQuery(''); searchInput.current?.focus(); }} aria-label="Clear search"><IconX size={16} stroke={1.8} /></button> : null}
          </label>
          <button type="button" className="sv-round sv-filter-button" onClick={() => { setFilterPicker(null); setSheet('filters'); }} aria-label={`Filters${filterCount ? `, ${filterCount} on` : ''}`}>
            <IconAdjustmentsHorizontal size={19} stroke={1.7} />
            {filterCount ? <span className="sv-count-dot">{filterCount}</span> : null}
          </button>
          <div className="sv-views sv-desktop-only" role="group" aria-label="Layout">
            <button type="button" aria-pressed={view === 'showcase'} onClick={() => setView('showcase')} aria-label="Showcase"><IconCube size={18} stroke={1.7} /></button>
            <button type="button" aria-pressed={view === 'wall'} onClick={() => setView('wall')} aria-label="Wall"><IconLayoutGrid size={18} stroke={1.7} /></button>
          </div>
          <button type="button" className="sv-round sv-mobile-only" aria-label="Browse options" onClick={() => setSheet('options')}><IconDots size={21} stroke={1.7}/></button>
          {onChangeCategory && <button type="button" className="sv-edit-toggle" aria-label={editMode ? 'Finish editing categories' : 'Edit categories'} aria-pressed={editMode} disabled={categoryBusy} onClick={() => { focusKey.current = current?.key; if (editMode) releaseRetainedSlab(); else goToIndex(index); setEditMode(value => !value); setEditFeedback(null); }}><IconPencil size={16} stroke={1.7} aria-hidden="true"/><span>{editMode ? 'Done' : 'Edit'}</span></button>}
        </div>
        <div className="sv-summary">
          <p aria-live="polite" title={`${filters.priceBasis || 'PSA'} same-grade last sales, with Shopify asking prices where no sale is cached. This total covers the current view, not necessarily the entire collection. Available values only; sale ranges use their lower end.`}>
            <span className="sv-total-value">
              <strong aria-label={totalHidden ? 'Total value hidden' : undefined}>{totalHidden ? '••••••' : money(summary.valued ? summary.valueLow : null)}</strong>
              <button type="button" className="sv-total-toggle" onClick={toggleTotal} aria-label={totalHidden ? 'Show total value' : 'Hide total value'} title={totalHidden ? 'Show total value' : 'Hide total value'} aria-pressed={totalHidden}>
                {totalHidden ? <IconEye size={18} stroke={1.7} aria-hidden="true" /> : <IconEyeOff size={18} stroke={1.7} aria-hidden="true" />}
              </button>
            </span>
            <span>
              {filteredResults.length === scoped.length ? `${count(summary.count)} slabs` : `${count(summary.count)} of ${count(scoped.length)} slabs`}
            </span>
          </p>
          <div className="sv-summary-actions">
          {notices.length > 0 && <button type="button" ref={updatesTrigger} className="sv-updates-toggle" aria-label={`View ${count(notices.length)} updates`} onClick={() => setSheet('updates')}>
            <IconBell size={16} stroke={1.7} aria-hidden="true" />
            <span className="sv-updates-label">Updates</span><span role="status" aria-label={`${count(notices.length)} browse updates`}>{count(notices.length)}</span>
          </button>}
          <label className="sv-sort">
            <span className="sv-sr">Sort</span>
            <select value={sort} onChange={(event) => setSort(event.target.value)}>
              {SORTS.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
            </select>
            <IconSelector size={14} stroke={1.8} aria-hidden="true" />
          </label>
          </div>
        </div>
        <div className="sv-browse-tools">
          <button type="button" className="sv-build-trade" disabled={status !== 'ready'} onClick={() => setSheet('trade')}><IconArrowsExchange size={16} stroke={1.8} />Build a trade</button>
          {pickBuilder && <button type="button" className="sv-build-trade" disabled={status !== 'ready'} onClick={() => setSheet('pick')}><IconCheck size={16} stroke={1.8} />Create pick list</button>}
        {quickFilters.length ? (
          <div className="sv-chips" role="group" aria-label="Quick filters">
            {quickFilters.map((chip) => (
              <button key={chip.id} type="button" className="sv-choice" aria-pressed={chip.active(filters)} onClick={() => setFilters(chip.toggle)}>
                {chip.label}{chip.count != null && <span>{count(chip.count)}</span>}
              </button>
            ))}
          </div>
        ) : null}
        </div>
        {status === 'error' && all.length > 0 && <p className="sv-notice" role="alert">Inventory may be out of date. Refresh failed{error ? `: ${error}` : '.'}</p>}
        {keptOutsideFilter && <p className="sv-notice" role="status">This slab no longer matches your filters. It stays here until you move on.</p>}
      </header>

      <div className="sv-body">{body}</div>
      {view === 'wall' && categoryEditor && <div className="sv-wall-editor"><span>{current.name}</span>{categoryEditor}</div>}

      <AnimatePresence mode="wait">
        {sheet === 'updates' && <Sheet key="updates" label="Browse updates" title={<h2>Updates</h2>} wide={wide} onClose={closeSheet}>
          <div className="sv-updates-content">
            {shopify && <p>Accepted Shopify requests may still be processing. Open For Shopify to check their current status.</p>}
            <ul className="sv-updates-list">{notices.map((notice, position) => <li key={position}>{notice}</li>)}</ul>
            {shopify && <a className="sv-secondary" href="/slab-inventory/shopify">Open For Shopify<IconExternalLink size={16} aria-hidden="true" /></a>}
          </div>
        </Sheet>}
        {sheet === 'options' && <Sheet key="options" className="sv-options-sheet" label="Browse options" title={<h2>Browse options</h2>} wide={wide} onClose={() => setSheet(null)}>
          <div className="sv-options">
            <div className="sv-options-layout" role="group" aria-label="Layout">
              <button type="button" className="sv-secondary" aria-pressed={view === 'showcase'} onClick={() => { setView('showcase'); setSheet(null); }}><IconCube size={20}/>Slab view</button>
              <button type="button" className="sv-secondary" aria-pressed={view === 'wall'} onClick={() => { setView('wall'); setSheet(null); }}><IconLayoutGrid size={20}/>Grid view</button>
            </div>
            <button type="button" className="sv-secondary" disabled={status !== 'ready'} onClick={() => setSheet('trade')}><IconArrowsExchange size={19}/>Build a trade</button>
            {pickBuilder && <button type="button" className="sv-secondary" disabled={status !== 'ready'} onClick={() => setSheet('pick')}><IconCheck size={19}/>Create pick list</button>}
            {onRetry && <><button type="button" className="sv-secondary" disabled={status === 'loading'} onClick={onRetry}><IconRefresh size={19}/>{status === 'loading' ? 'Refreshing inventory…' : 'Refresh inventory'}</button><small>Inventory can change while you browse. Refresh to check the latest records.</small></>}
            {current && <button type="button" className="sv-secondary" onClick={openDetails}>Slab details<IconChevronRight size={18}/></button>}
          </div>
        </Sheet>}
        {sheet === 'trade' && <Sheet key="trade" className="sv-trade-sheet" label="Build a trade" title={<h2>Build a trade</h2>} wide={wide} onClose={() => setSheet(null)}><TradeBuilder slabs={canonical} currentSlabs={filteredResults} draftKey={tradeDraftKey} /></Sheet>}
        {sheet === 'pick' && pickBuilder && <Sheet key="pick" className="sv-pick-sheet" label="Create a pick list" title={<h2>Create a pick list</h2>} wide={wide} onClose={() => setSheet(null)}><PickBuilder slabs={filteredResults} allSlabs={canonical} hiddenCerts={canonical.filter(slab => isHiddenByInventoryFilters(slab, filters)).map(slab => slab.cert)} {...pickBuilder} /></Sheet>}
        {sheet==='sale-link'&&saleTarget&&<Sheet key="sale-link" label="Add sale link" title={<h2>Add sale link</h2>} wide={wide} onClose={()=>setSheet(null)}><SaleLinkForm slab={saleTarget} onRequest={onSaleLink}/></Sheet>}
        {sheet==='shopify'&&shopifyTarget&&shopify&&<Sheet key="shopify" label="Shopify listing" title={<h2>Shopify listing</h2>} wide={wide} onClose={closeSheet}><SlabShopifyForm key={`${shopify.email}:${shopifyTarget.key}`} slab={shopifyTarget} {...shopify}/></Sheet>}
        {sheet==='category'&&categoryTarget&&<Sheet key="category" label="Change inventory category" title={<h2>Change category</h2>} wide={wide} onClose={()=>{if(!categoryBusy)setSheet(null);}}>
          <div className="sv-category-editor"><p><strong>{categoryTarget.name}</strong> · #{categoryTarget.cert}</p><p>Choose what this slab is for. Its safe location and prices stay the same. Shopify here earmarks it; it does not publish a listing.</p>
          <div className="sv-category-options">{Object.entries(INTENDED_USES).map(([value,label])=><button key={value} type="button" className="sv-secondary" aria-pressed={categoryTarget.intendedUse===value} disabled={categoryBusy||categoryTarget.intendedUse===value} onClick={()=>saveCategory(value)}>{label}{categoryTarget.intendedUse===value?' ✓':''}</button>)}</div>
          {categoryBusy&&<p role="status">Saving category…</p>}{categoryError&&<><p role="alert">{categoryError}</p>{onRetry&&<button type="button" className="sv-secondary" onClick={async()=>{await onRetry();setSheet(null);}}>Refresh inventory</button>}</>}
          </div>
        </Sheet>}
        {sheet === 'filters' ? (
          <Sheet
            key="filters"
            className="sv-filter-sheet"
            label={filterPicker ? `${FILTER_TITLES[filterPicker]} filter` : 'Filters'}
            title={<h2>{filterPicker ? FILTER_TITLES[filterPicker] : 'Filters'}</h2>}
            onBack={filterPicker ? () => setFilterPicker(null) : undefined}
            wide={wide}
            onClose={() => setSheet(null)}
            footer={(
              <>
                {filterPicker ? <button type="button" className="sv-secondary" disabled={!selectedFilterValues(filters, filterPicker).length} onClick={() => setFilters(previous => ({ ...previous, [filterPicker]: filterPicker === 'source' ? 'all' : [] }))}>Clear</button> : <button type="button" className="sv-secondary" onClick={() => setFilters(EMPTY_FILTERS)} disabled={!filterCount}>Reset</button>}
                <button type="button" className="sv-primary" onClick={() => setSheet(null)}>{filteredResults.length ? `Show ${count(filteredResults.length)} slabs` : 'No slabs match'}</button>
              </>
            )}
          >
            <SlabFilters facets={facets} filters={filters} onChange={setFilters} picker={filterPicker} onPickerChange={setFilterPicker} />
            {!filterPicker&&onRetry&&<section className="sv-details-group"><h3>Inventory updates</h3><p>Inventory can change while you browse. Refresh to check the latest records.</p><button type="button" className="sv-secondary" disabled={status === 'loading'} onClick={onRetry}>{status === 'loading' ? 'Refreshing inventory…' : 'Refresh inventory'}</button></section>}
            {!filterPicker&&onSyncSubmissions&&<section className="sv-details-group"><h3>Your ACE submissions</h3><button type="button" className="sv-secondary" disabled={submissionSync?.busy} onClick={onSyncSubmissions}>{submissionSync?.busy?'Checking…':'Refresh my submission details'}</button>{submissionSync?.message&&<p role="status">{submissionSync.message}</p>}</section>}
          </Sheet>
        ) : null}
        {sheet === 'details' && current ? (
          <Sheet
            key="details"
            label={`${current.name} details`}
            title={(
              <div className="sv-details-title">
                <div>
                  <h2>{current.name}</h2>
                  <p>{[current.set, current.number ? `#${current.number}` : ''].filter(Boolean).join(' ')}</p>
                </div>
                <GradeMark slab={current} size="small" />
              </div>
            )}
            wide={wide}
            onClose={() => setSheet(null)}
          >
            <SlabRevealAction control={revealControl} />
            {whatnot&&<p><a className="sv-secondary" href={`/slab-inventory/whatnot?cert=${encodeURIComponent(current.cert)}`}>Send to Whatnot inventory</a></p>}
            {shopify&&<div className="sv-details-shopify"><button type="button" className="sv-primary" onClick={openShopify}>Shopify listing<IconChevronRight size={16}/></button></div>}
            <div className="sv-detail-actions sv-mobile-only">
              {onChangeCategory&&current.source==='business'&&<button type="button" className="sv-secondary" onClick={openCategory} aria-label={`Change category: ${current.category}`}>Category · {current.category}<IconChevronRight size={16}/></button>}
              {onSaleLink&&<button type="button" className="sv-secondary" onClick={openSaleLink}>Add sale link<IconChevronRight size={16}/></button>}
            </div>
            {purchaseCosts&&current.source==='business'&&current.id&&<SlabPurchaseCost key={current.cert} slab={current} control={purchaseCosts}/>}
            {onPriceCheck&&<SlabPriceCheck key={current.cert} slab={current} onRequest={onPriceCheck} onRead={onPriceRead} onRefresh={onRetry}/>}
            <SlabDetails slab={current} fx={fx} />
          </Sheet>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
